import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { 
  ChevronLeft, 
  User,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Trophy,
  Calendar,
  BarChart3,
  Minus
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PlayerDetail() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get return info from URL params
  const fromGame = searchParams.get('from_game');
  const playerName = searchParams.get('name');
  const teamName = searchParams.get('team');
  const leagueId = searchParams.get('league');

  useEffect(() => {
    fetchPlayerData();
  }, [playerId, fromGame, leagueId]);

  const fetchPlayerData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build ESPN API URL with optional game_id for live stats
      let url = `${API_URL}/api/players/espn/${playerId}`;
      const params = new URLSearchParams();
      if (fromGame) params.append('game_id', fromGame);
      if (leagueId) params.append('league', leagueId);
      if (params.toString()) url += `?${params.toString()}`;
      
      console.log('[PlayerDetail] Fetching from:', url);
      
      const response = await axios.get(url);
      const data = response.data;
      
      // Transform ESPN response to our expected format
      setPlayer({
        name: data.player?.name || playerName,
        team: data.player?.team || teamName,
        position: data.player?.position,
        jersey: data.player?.jersey,
        headshot: data.player?.headshot,
        height: data.player?.height,
        weight: data.player?.weight,
        age: data.player?.age,
        experience: data.player?.experience,
        // Stats based on game state
        stats_type: data.stats_type, // 'season', 'live', or 'game'
        season_stats: data.season_stats || {},
        live_stats: data.live_stats,
        game_stats: data.game_stats,
        career_stats: data.career_stats || {},
        recent_games: (data.recent_games || []).map(g => ({
          date: g.date,
          opponent: g.opponent || g.opponent_abbr,
          result: g.result,
          score: g.score,
          stats: g.stats || {}
        })),
        splits: data.splits || {}
      });
      
    } catch (err) {
      console.error('Failed to load player from ESPN:', err);
      // Don't set error - we'll show info from URL params
      setError('espn_unavailable');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (fromGame) {
      // Navigate back to game H2H tab and scroll to player section
      navigate(`/game/${fromGame}?tab=h2h&scrollTo=players`);
    } else {
      navigate(-1);
    }
  };

  const getTrendIcon = (value, baseline = 0) => {
    if (value > baseline) return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (value < baseline) return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  // League-specific stat labels
  const getStatLabels = (league) => {
    const labels = {
      nba: { PTS: 'Points', AST: 'Assists', REB: 'Rebounds', STL: 'Steals', BLK: 'Blocks', FG_PCT: 'FG%', MIN: 'Minutes' },
      nhl: { G: 'Goals', A: 'Assists', PTS: 'Points', PM: '+/-', SOG: 'Shots', TOI: 'Time on Ice' },
      mlb: { AVG: 'Batting Avg', HR: 'Home Runs', RBI: 'RBIs', R: 'Runs', H: 'Hits', ERA: 'ERA', W: 'Wins', SO: 'Strikeouts' },
      nfl: { YDS: 'Yards', TD: 'Touchdowns', INT: 'Interceptions', CMP: 'Completions', RTG: 'Rating' },
      mls: { G: 'Goals', A: 'Assists', SOT: 'Shots on Target', FC: 'Fouls', YC: 'Yellow Cards' },
      epl: { G: 'Goals', A: 'Assists', SOT: 'Shots on Target', FC: 'Fouls', YC: 'Yellow Cards' }
    };
    return labels[league?.toLowerCase()] || labels.nba;
  };

  const statLabels = getStatLabels(leagueId);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 py-6 max-w-4xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
          data-testid="back-button"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {fromGame ? 'Back to Game' : 'Back'}
        </Button>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Player Header - Always show with data from URL params if API fails */}
            <Card data-testid="player-header">
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  {/* Player Photo */}
                  <div className="relative">
                    {player?.headshot ? (
                      <img 
                        src={player.headshot} 
                        alt={player?.name || playerName}
                        className="w-24 h-24 rounded-full object-cover border-2 border-primary/30"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-24 h-24 bg-secondary rounded-full flex items-center justify-center border-2 border-primary/30">
                        <User className="w-12 h-12 text-muted-foreground" />
                      </div>
                    )}
                    {/* Live indicator */}
                    {player?.stats_type === 'live' && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                        <span className="text-[10px] text-white font-bold">LIVE</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Player Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h1 className="text-2xl font-display font-bold uppercase tracking-tight">
                        {player?.name || playerName || 'Unknown Player'}
                      </h1>
                      {player?.stats_type === 'live' && (
                        <Badge className="bg-red-500 text-white animate-pulse">LIVE</Badge>
                      )}
                      {player?.stats_type === 'game' && (
                        <Badge variant="secondary">Game Stats</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      {(player?.team || teamName) && (
                        <Badge variant="outline" className="text-primary border-primary/30">
                          {player?.team || teamName}
                        </Badge>
                      )}
                      {player?.position && (
                        <Badge variant="secondary">{player.position}</Badge>
                      )}
                      {player?.jersey && (
                        <span className="text-lg font-mono font-bold text-muted-foreground">
                          #{player.jersey}
                        </span>
                      )}
                      {player?.height && (
                        <span className="text-sm text-muted-foreground">{player.height}</span>
                      )}
                      {player?.weight && (
                        <span className="text-sm text-muted-foreground">{player.weight}</span>
                      )}
                    </div>
                    {leagueId && (
                      <Badge className="mt-2 uppercase">{leagueId}</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Error Message or Player Stats */}
            {error === 'espn_unavailable' ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">
                    Detailed player statistics are not available at this time.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Player data is updated during the active season. Check back later for updated stats.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* LIVE Game Stats - Show prominently when game is live */}
                {player?.stats_type === 'live' && player?.live_stats && Object.keys(player.live_stats).length > 0 && (
                  <Card data-testid="live-stats" className="border-red-500/50 bg-red-500/5">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-red-500">
                        <Activity className="w-5 h-5 animate-pulse" />
                        Live Game Stats
                        <Badge className="bg-red-500 text-white text-xs animate-pulse ml-2">LIVE</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                        {Object.entries(player.live_stats).slice(0, 12).map(([key, value]) => (
                          <div key={key} className="text-center p-3 bg-red-500/10 rounded-sm border border-red-500/20">
                            <p className="text-xs text-red-400 uppercase">
                              {statLabels[key] || key}
                            </p>
                            <p className="text-xl font-mono font-bold mt-1 text-red-500">
                              {value}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Game Stats - Show when viewing finished game */}
                {player?.stats_type === 'game' && player?.game_stats && Object.keys(player.game_stats).length > 0 && (
                  <Card data-testid="game-stats" className="border-blue-500/50 bg-blue-500/5">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-blue-500">
                        <BarChart3 className="w-5 h-5" />
                        Game Stats
                        <Badge variant="secondary" className="ml-2">Final</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                        {Object.entries(player.game_stats).slice(0, 12).map(([key, value]) => (
                          <div key={key} className="text-center p-3 bg-blue-500/10 rounded-sm border border-blue-500/20">
                            <p className="text-xs text-blue-400 uppercase">
                              {statLabels[key] || key}
                            </p>
                            <p className="text-xl font-mono font-bold mt-1 text-blue-500">
                              {value}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Season Stats */}
                {player?.season_stats && Object.keys(player.season_stats).length > 0 && (
                  <Card data-testid="season-stats">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5" />
                        Season Statistics
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                        {Object.entries(player.season_stats).slice(0, 12).map(([key, value]) => (
                          <div key={key} className="text-center p-3 bg-secondary/30 rounded-sm">
                            <p className="text-xs text-muted-foreground uppercase">
                              {statLabels[key] || key}
                            </p>
                            <p className="text-xl font-mono font-bold mt-1">
                              {typeof value === 'number' ? (value % 1 !== 0 ? value.toFixed(1) : value) : value}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Performance */}
                {player?.recent_games && player.recent_games.length > 0 && (
                  <Card data-testid="recent-games">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Recent Games
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {player.recent_games.map((game, idx) => (
                          <div key={idx} className="p-3 bg-secondary/20 rounded-sm">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{game.opponent}</span>
                                <Badge variant={game.result === 'W' ? 'default' : 'secondary'} 
                                       className={game.result === 'W' ? 'bg-green-500' : 'bg-red-500'}>
                                  {game.result}
                                </Badge>
                              </div>
                              <span className="text-xs text-muted-foreground">{game.date}</span>
                            </div>
                            {game.stats && (
                              <div className="flex flex-wrap gap-3">
                                {Object.entries(game.stats).slice(0, 6).map(([key, value]) => (
                                  <div key={key} className="text-center">
                                    <span className="text-xs text-muted-foreground">{statLabels[key] || key}: </span>
                                    <span className="text-sm font-mono font-medium">{value}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* vs Opponent Performance */}
                {player?.vs_opponent && (
                  <Card data-testid="vs-opponent">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        Performance vs {player.vs_opponent.opponent_name || 'Opponent'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Games Played</p>
                          <p className="text-3xl font-mono font-bold">{player.vs_opponent.games || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Avg Performance</p>
                          <div className="flex items-center gap-2">
                            {getTrendIcon(player.vs_opponent.performance_trend)}
                            <span className="text-sm">
                              {player.vs_opponent.performance_trend > 0 ? 'Above' : 
                               player.vs_opponent.performance_trend < 0 ? 'Below' : 'At'} Season Avg
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {player.vs_opponent.avg_stats && (
                        <div className="mt-4 pt-4 border-t border-border/50">
                          <p className="text-sm text-muted-foreground mb-3">Average Stats vs Opponent</p>
                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                            {Object.entries(player.vs_opponent.avg_stats).map(([key, value]) => (
                              <div key={key} className="text-center p-2 bg-secondary/20 rounded">
                                <p className="text-xs text-muted-foreground">{statLabels[key] || key}</p>
                                <p className="text-lg font-mono font-medium">{value}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Career Highlights */}
                {player?.highlights && player.highlights.length > 0 && (
                  <Card data-testid="highlights">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Trophy className="w-5 h-5" />
                        Career Highlights
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {player.highlights.map((highlight, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Activity className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-sm">{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* No Data Message - Only show if API succeeded but returned no data */}
                {!player?.season_stats && !player?.recent_games && !player?.vs_opponent && !error && (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        Detailed player statistics are not available at this time.
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Check back during the active season for updated stats.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
}
